import classNames from 'classnames'
import { Field, useField, useFormikContext } from 'formik'
import React, { useCallback } from 'react'
import PhoneInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'
import { ITelephoneField } from '../@types/FormField'
import { sanitizeText } from '../Field'
import { IFormFieldProps } from '../Field.interface'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'

const FieldPhone = React.memo((props: IFormFieldProps<ITelephoneField>) => {
  const fieldId = `${props.id}_field`

  const [fieldData] = useField(props.id)
  const { setFieldValue } = useFormikContext()

  const handleChange = useCallback((value: string) => {
    // For some reason props.onChange doesnt seems to be changing formik value
    fieldData.onChange(value ?? '')
    props.onChange({ target: { name: props.id } } as unknown as React.ChangeEvent)
    setFieldValue(props.id, value ?? '')
  }, [])

  return (
    <PhoneInput
      id={fieldId}
      defaultCountry="US"
      placeholder={props.placeholder}
      value={fieldData.value}
      onChange={handleChange}
      flags={flags}
    />
  )
})

export const PhoneField = React.memo((props: IFormFieldProps<ITelephoneField>) => {
  const fieldId = `${props.id}_field`
  const labelId = `${props.id}_label`

  const labelText = props.label && sanitizeText(props.label)

  const labelClassNames = classNames('form-field-phone', {
    'has-error': props.enableValidation && props.hasError,
  })

  const { getTranslatedValidationErrorIfAny } = useTranslate()

  return (
    <>
      <label id={labelId} className={labelClassNames} htmlFor={fieldId}>
        {labelText && <span className="label-text">{labelText}</span>}

        <div className={'phone-input'}>
          <Field
            name={props.id}
            validate={(value: string) => getTranslatedValidationErrorIfAny(props, value)}
          >
            {() => {
              return <FieldPhone {...props} />
            }}
          </Field>
        </div>
      </label>
      <style jsx>{`
        .label-text {
          display: block;
          margin-bottom: 10px;
        }
      `}</style>
      <style jsx global>
        {`
          .phone-input {
            width: 100%;
            border-radius: 30px;
            border: 2px solid #000;
            background: #fff;

            .PhoneInput {
              display: flex;
              align-items: center;

              input.PhoneInputInput {
                padding: 14px;
                border: 0;
                width: 100%;
                border-radius: 0 30px 30px 0;

                &:focus {
                  outline: 0;
                }
              }
            }

            .PhoneInputCountryIcon {
              width: calc(1em * 1.5);
              height: 1em;
            }

            .PhoneInputCountryIcon--square {
              width: 1em;
            }

            .PhoneInputCountryIconImg {
              width: 100%;
              height: 100%;
            }

            .PhoneInputInternationalIconPhone {
              opacity: 0.8;
            }

            .PhoneInputInternationalIconGlobe {
              opacity: 0.65;
            }

            .PhoneInputCountry {
              position: relative;
              display: flex;
              align-items: center;
              padding: 15px;
            }

            .PhoneInputCountrySelect {
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              width: 100%;
              z-index: 1;
              border: 0;
              opacity: 0;
              cursor: pointer;
            }

            .PhoneInputCountrySelect[disabled] {
              cursor: default;
            }

            .PhoneInputCountrySelectArrow {
              content: '';
              width: 8px;
              height: 8px;
              border-style: solid;
              border-width: 8px 6px 0 6px;
              border-color: $black-legacy transparent transparent transparent;
              margin-left: 10px;
            }

            .PhoneInputCountrySelect:focus
              + .PhoneInputCountryIcon
              + .PhoneInputCountrySelectArrow {
              opacity: 1;
              color: $teal-legacy;
            }

            .PhoneInputCountrySelect:focus + .PhoneInputCountryIcon--border {
              outline: 2px solid black;
              outline-offset: 2px;
            }

            .PhoneInputCountrySelect:focus
              + .PhoneInputCountryIcon
              .PhoneInputInternationalIconGlobe {
              opacity: 1;
              color: $teal-legacy;
            }
          }
        `}
      </style>
    </>
  )
})
